import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Camera, FloppyDisk, Key } from "@phosphor-icons/react";

function csv(arr) { return Array.isArray(arr) ? arr.join(", ") : (arr || ""); }
function fromCsv(s) { return String(s || "").split(",").map((v) => v.trim()).filter(Boolean); }

const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({
    name: "", phone: "", bio: "", photo_url: "",
    languages: "", specializations: "",
  });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef();

  // Seed the form from the currently-logged-in user (single source of truth).
  useEffect(() => {
    if (!user) return;
    setF({
      name: user.name || "",
      phone: user.phone || "",
      bio: user.bio || "",
      photo_url: user.photo_url || "",
      languages: csv(user.languages),
      specializations: csv(user.specializations),
    });
  }, [user]);

  if (!user) return null;

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", {
        name: f.name, phone: f.phone, bio: f.bio, photo_url: f.photo_url,
        languages: fromCsv(f.languages),
        specializations: fromCsv(f.specializations),
      });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function uploadPhoto() {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a photo file first");
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((p) => ({ ...p, photo_url: data.photo_url }));
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setPhotoBusy(false); }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (!pw.current || !pw.next) return toast.error("Both current and new passwords are required");
    if (pw.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.next !== pw.confirm) return toast.error("New password and confirmation don't match");
    setPwSaving(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: pw.current, new_password: pw.next,
      });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password changed — use the new password next time you sign in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally { setPwSaving(false); }
  }

  const roleLabel = user.role === "founding_partner" ? "Founding Partner"
                  : user.role === "admin" ? "Administrator" : "Advisor";

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="admin-profile">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Account · {roleLabel}</div>
        <h1 className="font-serif text-3xl lg:text-4xl">My profile</h1>
        <p className="text-sm text-gray-500 mt-2">Update your public bio, contact details and headshot. Changes go live on <a href={`/agents/${user.id}`} target="_blank" rel="noreferrer" className="text-[#001588] underline">your public page</a> instantly.</p>
      </div>

      {/* Details */}
      <form onSubmit={saveProfile} className="bg-white border border-[#E5E7EB] p-6 lg:p-8 mb-8" data-testid="profile-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Email (read-only)</span>
            <input value={user.email} readOnly className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} data-testid="profile-email" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Full name *</span>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className={inputCls} data-testid="profile-name" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Phone</span>
            <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="profile-phone" placeholder="+971 55 …" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Bio</span>
            <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows="4" className={inputCls} data-testid="profile-bio" placeholder="Tell clients how you help them buy, rent or invest in Dubai." />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Languages (comma sep)</span>
            <input value={f.languages} onChange={(e) => setF({ ...f, languages: e.target.value })} className={inputCls} data-testid="profile-languages" placeholder="English, Arabic, Russian" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Specialisations (comma sep)</span>
            <input value={f.specializations} onChange={(e) => setF({ ...f, specializations: e.target.value })} className={inputCls} data-testid="profile-specializations" placeholder="Palm Jumeirah, Off-plan" />
          </label>
        </div>

        <div className="mt-8 pt-6 border-t border-neutral-200">
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-4">Headshot</div>
          <div className="flex items-center gap-5">
            {f.photo_url ? (
              <img src={f.photo_url} alt="preview" className="w-20 h-20 object-cover rounded-full" />
            ) : (
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-neutral-400"><Camera size={22} /></div>
            )}
            <div className="flex-1">
              <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="text-sm" data-testid="profile-photo-file" />
              <div className="text-xs text-gray-500 mt-1">PNG, JPG or WEBP · square works best · under 5 MB</div>
            </div>
            <button type="button" onClick={uploadPhoto} disabled={photoBusy} className="px-5 py-2 border border-[#001588] text-[#001588] text-xs uppercase tracking-wider hover:bg-[#001588] hover:text-white disabled:opacity-50" data-testid="profile-photo-upload">
              {photoBusy ? "Uploading…" : "Upload"}
            </button>
          </div>
          <label className="block mt-4">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">Or paste a URL</span>
            <input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} className={inputCls} data-testid="profile-photo-url" placeholder="https://…/photo.jpg" />
          </label>
        </div>

        <div className="mt-8 flex gap-3">
          <button type="submit" disabled={saving} className="flex items-center gap-2 bg-[#001588] text-white px-6 py-3 text-sm hover:bg-black disabled:opacity-50" data-testid="profile-save">
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      {/* Password */}
      <form onSubmit={changePassword} className="bg-white border border-[#E5E7EB] p-6 lg:p-8" data-testid="password-form">
        <div className="flex items-center gap-2 mb-6">
          <Key size={20} weight="thin" className="text-[#001588]" />
          <h2 className="font-serif text-2xl">Change password</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block sm:col-span-3">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Current password *</span>
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required className={inputCls} data-testid="password-current" autoComplete="current-password" />
          </label>
          <label className="block sm:col-span-3 lg:col-span-3">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">New password * <span className="text-gray-400 normal-case">(8+ characters)</span></span>
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={8} className={inputCls} data-testid="password-new" autoComplete="new-password" />
          </label>
          <label className="block sm:col-span-3">
            <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Confirm new password *</span>
            <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={8} className={inputCls} data-testid="password-confirm" autoComplete="new-password" />
          </label>
        </div>
        <div className="mt-6">
          <button type="submit" disabled={pwSaving} className="flex items-center gap-2 bg-[#001588] text-white px-6 py-3 text-sm hover:bg-black disabled:opacity-50" data-testid="password-save">
            <Key size={16} /> {pwSaving ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}
